<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { Eye, Plus, Loader2, Check, ChevronLeft, ChevronRight } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter
  } from '@/components/ui/dialog'
  import type { TForm, TQuestionnaire } from '@/types/generated'
  import { useLawTopicTranslation } from '@/composables/useLawTopicTranslation'
  import { useLawFieldTranslation } from '@/composables/useLawFieldTranslation'

  const { translateLawTopic } = useLawTopicTranslation()
  const { translateLawField } = useLawFieldTranslation()

  const props = defineProps<{
    modelValue: boolean
    title: string
    description: string
    items: TForm[] | TQuestionnaire[]
    isLoading: boolean
    searchPlaceholder?: string
    initialSelectedItems?: Array<TForm | TQuestionnaire>
    totalCount?: number
    currentPage?: number
    pageSize?: number
  }>()

  const emit = defineEmits<{
    'update:modelValue': [value: boolean]
    'select-items': [items: any[]]
    'preview-item': [item: any]
    'page-change': [page: number]
    search: [query: string]
  }>()

  const search = ref('')
  const selectedItems = ref<Array<TForm | TQuestionnaire>>(props.initialSelectedItems || [])
  const searchDebounceTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

  const totalPages = computed(() => {
    if (!props.totalCount || !props.pageSize) return 1
    return Math.ceil(props.totalCount / props.pageSize)
  })

  const showPagination = computed(() => {
    return props.totalCount !== undefined && totalPages.value > 1
  })

  const isServerSideSearch = computed(() => props.totalCount !== undefined)

  const filteredItems = computed(() => {
    // When server-side pagination/search is configured, always use server results
    if (isServerSideSearch.value) return props.items
    if (!search.value) return props.items
    const searchQuery = search.value.toLowerCase()
    return props.items.filter((item) => {
      const searchText = (item.title || (item as TForm).document_name || '').toLowerCase()
      return searchText.includes(searchQuery)
    })
  })

  const getItemId = (item: TForm | TQuestionnaire) => {
    return 'uuid' in item ? item.uuid : item.id
  }

  const close = () => {
    emit('update:modelValue', false)
    search.value = ''
    selectedItems.value = []
  }

  // Watch for dialog open/close to reset state
  watch(
    () => props.modelValue,
    (newValue) => {
      if (!newValue) {
        selectedItems.value = []
        search.value = ''
      } else {
        // Initialize selections when dialog opens
        selectedItems.value = props.initialSelectedItems?.length
          ? [...props.initialSelectedItems]
          : []
      }
    }
  )

  const toggleSelect = (item: TForm | TQuestionnaire) => {
    const index = selectedItems.value.findIndex((i) => getItemId(i) === getItemId(item))
    if (index === -1) {
      selectedItems.value = [...selectedItems.value, item]
    } else {
      selectedItems.value = selectedItems.value.filter((i) => getItemId(i) !== getItemId(item))
    }
  }

  const isSelected = (item: TForm | TQuestionnaire) => {
    return selectedItems.value.some((i) => getItemId(i) === getItemId(item))
  }

  const handleAttach = () => {
    if (selectedItems.value.length > 0) {
      emit('select-items', selectedItems.value)
      close()
    }
  }

  const handlePreview = (item: any) => {
    emit('preview-item', item)
  }

  const handleSearch = (event: Event) => {
    const value = (event.target as HTMLInputElement).value
    search.value = value

    if (searchDebounceTimeout.value) {
      clearTimeout(searchDebounceTimeout.value)
    }

    // Always emit search when server-side pagination is configured
    if (isServerSideSearch.value) {
      searchDebounceTimeout.value = setTimeout(() => {
        emit('search', value)
      }, 300)
    }
  }

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages.value && page !== props.currentPage) {
      emit('page-change', page)
    }
  }

  watch(
    () => props.modelValue,
    (newValue) => {
      if (!newValue && searchDebounceTimeout.value) {
        clearTimeout(searchDebounceTimeout.value)
        searchDebounceTimeout.value = null
      }
    }
  )
</script>

<template>
  <Dialog
    :open="modelValue"
    @update:open="close"
  >
    <DialogContent class="sm:max-w-[800px]">
      <DialogHeader>
        <DialogTitle>{{ title }}</DialogTitle>
        <DialogDescription>
          {{ description }}
        </DialogDescription>
      </DialogHeader>

      <!-- Search Input -->
      <div class="relative w-full">
        <div class="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
          <svg
            class="size-4 text-gray-500"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle
              cx="11"
              cy="11"
              r="8"
            />
            <path d="m21 21-4.3-4.3" />
          </svg>
        </div>
        <input
          :value="search"
          type="text"
          class="block w-full rounded-lg border-gray-300 ps-10 focus:border-purple-500 focus:ring-purple-500"
          :placeholder="searchPlaceholder"
          @input="handleSearch"
        />
      </div>

      <!-- Items List -->
      <div
        class="relative max-h-[400px] divide-y divide-gray-200 overflow-y-auto py-4"
        data-cy="documentsList"
      >
        <!-- Loading overlay (only when items exist) -->
        <div
          v-if="isLoading && filteredItems.length > 0"
          class="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm"
        >
          <Loader2 class="h-6 w-6 animate-spin text-gray-500" />
        </div>

        <!-- Initial loading (no items yet) -->
        <div
          v-if="isLoading && filteredItems.length === 0"
          class="flex items-center justify-center py-4"
        >
          <Loader2 class="h-6 w-6 animate-spin text-gray-500" />
        </div>

        <!-- Empty state -->
        <div
          v-else-if="!isLoading && filteredItems.length === 0"
          class="flex items-center justify-center py-4"
        >
          <p class="text-sm text-gray-500">Keine Ergebnisse gefunden</p>
        </div>

        <!-- Items -->
        <div
          v-for="item in filteredItems"
          v-else
          :key="getItemId(item)"
          class="flex cursor-pointer items-center justify-between px-4 py-3 hover:bg-gray-50"
          :class="{ 'bg-gray-50': isSelected(item) }"
          @click="toggleSelect(item)"
        >
          <div class="flex items-center gap-2">
            <div
              class="h-4 w-4 rounded border border-gray-300"
              :class="{ 'border-purple-600 bg-purple-600': isSelected(item) }"
            >
              <Check
                v-if="isSelected(item)"
                class="h-4 w-4 text-white"
              />
            </div>
            <div class="flex items-center gap-2">
              <span class="truncate font-medium">
                {{ item.title || (item as any).document_name }}
              </span>
              <span
                v-if="'uuid' in item && ((item as any).law_field || (item as any).law_topic)"
                class="shrink-0 text-xs text-gray-500"
              >
                <span v-if="(item as any).law_field">
                  {{ translateLawField((item as any).law_field) }}
                </span>
                <span v-if="(item as any).law_field && (item as any).law_topic">/</span>
                <span v-if="(item as any).law_topic">
                  {{ translateLawTopic((item as any).law_topic) }}
                </span>
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              @click.stop="handlePreview(item)"
            >
              <Eye class="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              @click.stop="toggleSelect(item)"
            >
              <Plus
                class="h-4 w-4"
                :class="{ 'rotate-45': isSelected(item) }"
              />
            </Button>
          </div>
        </div>
      </div>

      <!-- Pagination Controls -->
      <div
        v-if="showPagination"
        class="flex items-center justify-center gap-2 py-2"
      >
        <Button
          variant="secondary"
          size="sm"
          :disabled="currentPage === 1"
          @click="goToPage(currentPage! - 1)"
        >
          <ChevronLeft class="h-4 w-4" />
        </Button>
        <span class="text-sm text-gray-700">Seite {{ currentPage }} von {{ totalPages }}</span>
        <Button
          variant="secondary"
          size="sm"
          :disabled="currentPage === totalPages"
          @click="goToPage(currentPage! + 1)"
        >
          <ChevronRight class="h-4 w-4" />
        </Button>
      </div>

      <!-- Footer -->
      <DialogFooter>
        <Button
          variant="secondary"
          @click="close"
        >
          Abbrechen
        </Button>
        <Button
          variant="default"
          :disabled="selectedItems.length === 0"
          data-cy="attachBtn"
          @click="handleAttach"
        >
          {{ selectedItems.length }} Anhängen
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
